// Pure parsers for the discount API payloads
// (/api/shopify/get-automatic-discount-by-id and get-tiered-discount-by-id).
// Behavior-matched to the parseDiscountsConditions copies inside the old
// useDiscountFreeItem / useDiscountGiftWithPurchase / useDiscountTiered hooks,
// which stay untouched for the flag-off path until cutover.

const asActiveArray = discounts =>
  (!Array.isArray(discounts) ? [discounts] : discounts).filter(d => d?.data?.status === 'ACTIVE');

export const parseGiftDiscounts = discounts =>
  asActiveArray(discounts)
    .map(discount => {
      const discountTitle = discount?.data?.title;
      const discountId = discount?.discountId;
      const condition = discount?.data?.customerBuys?.items;
      const value = discount?.data?.customerBuys?.value;
      const usesPerOrderLimit = discount?.data?.usesPerOrderLimit;
      const freeProducts = discount?.data?.customerGets?.items?.products?.edges?.map(({ node }) => {
        const { id, title, featuredImage, handle } = node || {};
        const variantId = node?.variants?.edges?.[0]?.node?.id;
        const available =
          (node?.status ?? 'ACTIVE') === 'ACTIVE' && !(node?.tags || []).includes('out-of-stock');
        return { id, variantId, title, featuredImage, handle, available };
      });

      const base = { discountTitle, discountId, value, freeProducts, usesPerOrderLimit };

      if (condition?.type === 'DiscountProducts') {
        return { ...base, products: condition?.products?.edges?.map(({ node }) => node?.id) };
      }
      if (condition?.type === 'DiscountCollections') {
        return {
          ...base,
          collections: condition?.collections?.edges?.map(({ node }) => node?.id),
          collectionTitles: condition?.collections?.edges?.map(({ node }) => node?.title),
        };
      }
      if (condition?.type === 'AllDiscountItems') {
        return { ...base, allItems: true };
      }
      return null;
    })
    .filter(Boolean);

export const parseTieredDiscounts = discounts =>
  asActiveArray(discounts)
    .map(discount => {
      const minimumRequirement = discount?.data?.minimumRequirement;
      const customerGetsItems = discount?.data?.customerGets?.items;
      const customerGetsValue = discount?.data?.customerGets?.value;

      const minimumQuantity =
        minimumRequirement?.type === 'DiscountMinimumQuantity'
          ? parseInt(minimumRequirement?.greaterThanOrEqualToQuantity, 10)
          : null;
      const minimumSubtotal =
        minimumRequirement?.type === 'DiscountMinimumSubtotal'
          ? parseFloat(minimumRequirement?.greaterThanOrEqualToSubtotal?.amount)
          : null;
      const discountPercentage =
        customerGetsValue?.type === 'DiscountPercentage' ? customerGetsValue?.percentage : null;
      const discountAmount =
        customerGetsValue?.type === 'DiscountAmount'
          ? parseFloat(customerGetsValue?.amount?.amount)
          : null;

      let eligibleProducts = null;
      let eligibleCollections = null;
      let allItems = false;
      if (customerGetsItems?.type === 'DiscountProducts') {
        eligibleProducts = customerGetsItems?.products?.edges?.map(({ node }) => node?.id);
      } else if (customerGetsItems?.type === 'DiscountCollections') {
        eligibleCollections = customerGetsItems?.collections?.edges?.map(({ node }) => ({
          id: node?.id,
          title: node?.title,
        }));
      } else if (customerGetsItems?.type === 'AllDiscountItems') {
        allItems = true;
      }

      return {
        discountTitle: discount?.data?.title,
        discountId: discount?.discountId,
        minimumQuantity,
        minimumSubtotal,
        discountPercentage,
        discountAmount,
        eligibleProducts,
        eligibleCollections,
        allItems,
      };
    })
    .filter(Boolean);
